// Travel stats, from GET /stats/me.
//
// THE RULE THIS SCREEN EXISTS TO KEEP: never render an invented number. A
// field the server sends as null is printed as "not available", never as
// zero. Zero is a claim ("you travelled nowhere"), absence is not, and
// showing one as the other lies to a traveller about their own record.
//
// When this API build has no /stats/me at all, the screen says so and falls
// back to figures counted from the data already on this device, under a
// heading that says exactly that. Those are still real counts of real
// entries, they are just this device's view rather than the full history.
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { apiClient } from "../lib/api";
import { formatDistance, formatNumber } from "../lib/format";
import { placeIdFor } from "../lib/places";
import type { TravelStats } from "../lib/types";
import { useApp } from "../state/app";
import { IconChart } from "@traveldiary/ui";

type LoadState = "loading" | "server" | "local" | "failed";

export function StatsScreen() {
  const { t, locale, moments, pendingMoments, trips, wishlist } = useApp();
  const [stats, setStats] = useState<TravelStats | null>(null);
  const [load, setLoad] = useState<LoadState>("loading");

  const reload = useCallback(async () => {
    setLoad("loading");
    try {
      const fresh = await apiClient.travelStats();
      if (!fresh) {
        setStats(null);
        setLoad("local");
        return;
      }
      setStats(fresh);
      setLoad("server");
    } catch {
      setStats(null);
      setLoad("failed");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Real counts of real records, but only the ones this device holds. */
  const local = useMemo(() => {
    const places = new Set<string>();
    let photos = 0;
    for (const moment of moments) {
      if (moment.lat !== null && moment.lng !== null) places.add(placeIdFor(moment.lat, moment.lng));
      photos += moment.media.length;
    }
    const days = new Set(moments.map((moment) => moment.capturedAt.slice(0, 10)));
    return {
      entries: moments.length + pendingMoments.length,
      places: places.size,
      trips: trips.length,
      days: days.size,
      photos,
      saved: wishlist.length,
    };
  }, [moments, pendingMoments, trips, wishlist]);

  if (load === "loading") {
    return (
      <div className="screen-pad">
        <p className="field-note">{t("common.loading")}</p>
      </div>
    );
  }

  if (load === "failed") {
    return (
      <div className="screen-pad">
        <EmptyState
          glyph={<IconChart />}
          title={t("stats.loadFailed")}
          hint={t("stats.nullNote")}
          action={
            <button type="button" className="btn btn-quiet" onClick={() => void reload()}>
              {t("common.retry")}
            </button>
          }
        />
      </div>
    );
  }

  if (load === "local" || !stats) {
    // Six tiles: 2 x 3 on a phone, 3 x 2 from 600px. Exact either way.
    const tiles: Array<[string, number]> = [
      [t("stats.entries"), local.entries],
      [t("stats.places"), local.places],
      [t("stats.trips"), local.trips],
      [t("stats.days"), local.days],
      [t("stats.photos"), local.photos],
      [t("stats.saved"), local.saved],
    ];
    return (
      <div className="screen-pad">
        <p className="field-note">{t("stats.unavailable")}</p>
        <p className="section-sub">{t("stats.localLede")}</p>
        <div className="tile-grid tile-grid-trio">
          {tiles.map(([label, value]) => (
            <div className="tile" key={label}>
              <span className="tile-label">{label}</span>
              <span className="tile-value">{formatNumber(value, locale)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const unavailable = t("common.unavailable");
  const count = (value: number | null) =>
    value === null ? null : formatNumber(value, locale);
  const distance = (value: number | null) =>
    value === null ? null : formatDistance(value, locale);

  // Six again, so the grid tiles exactly at both widths with no dead cell.
  // Each tile carries its own words for an absent figure: "farthest from
  // home" is missing for a KNOWN reason, that nothing in this product records
  // a home location, and saying so is more use than a blank "not available".
  const tiles: Array<[string, string | null, string]> = [
    [t("stats.entries"), count(stats.entries), unavailable],
    [t("stats.places"), count(stats.destinations), unavailable],
    [t("stats.trips"), count(stats.trips), unavailable],
    [t("stats.days"), count(stats.daysTravelled), unavailable],
    [t("stats.distance"), distance(stats.totalDistanceM), unavailable],
    [t("stats.farthest"), distance(stats.farthestFromHomeM), t("stats.needsHome")],
  ];

  const regions = stats.regions ?? [];
  const provinces = stats.provinces ?? [];

  return (
    <div className="screen-pad">
      <p className="section-sub">{t("stats.serverLede")}</p>

      <div className="tile-grid tile-grid-trio">
        {tiles.map(([label, value, absent]) => (
          <div className="tile" key={label}>
            <span className="tile-label">{label}</span>
            {/* An absent figure steps down one size rather than wrapping the
                tile into a different shape, the same way a currency range
                does on the budget screen. */}
            <span className={value === null ? "tile-value tile-value-sm" : "tile-value"}>
              {value ?? absent}
            </span>
          </div>
        ))}
      </div>
      <p className="field-note">{t("stats.nullNote")}</p>
      {stats.farthestFromHomeM === null ? (
        <p className="field-note">{t("stats.needsHomeNote")}</p>
      ) : null}

      <section className="section">
        <h2 className="section-title">{t("stats.regions")}</h2>
        {regions.length === 0 && provinces.length === 0 ? (
          <p className="section-sub">
            {stats.regions === null && stats.provinces === null
              ? unavailable
              : t("stats.regionsEmpty")}
          </p>
        ) : (
          <div className="chip-row">
            {[...new Set([...provinces, ...regions])].map((name) => (
              <span className="chip chip-static" key={name}>
                {name}
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
